/**
 * Development Role Override Utility
 * 
 * This module provides functionality to temporarily override the user's role
 * in development/localhost environments for testing purposes.
 * 
 * IMPORTANT: This only works in development mode (localhost)
 */

import { create } from 'zustand';
import { getCompanyRoles, getUserPermissions } from './permissions';

interface DevRoleOverrideState {
  overrideRoleId: string | null;
  setOverrideRole: (roleId: string | null) => void;
  clearOverride: () => void;
  isEnabled: () => boolean;
}

/**
 * Check if we're in development mode (localhost)
 */
export function isDevelopmentMode(): boolean {
  return window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' ||
         window.location.hostname.includes('.local');
}

/**
 * Zustand store for dev role override
 */
export const useDevRoleOverride = create<DevRoleOverrideState>((set, get) => ({
  overrideRoleId: null,
  
  setOverrideRole: (roleId: string | null) => {
    if (!isDevelopmentMode()) {
      console.warn('⚠️ [DevRoleOverride] Role override only works in development mode');
      return;
    }
    const prevRoleId = get().overrideRoleId;
    set({ overrideRoleId: roleId });
    
    if (roleId) {
      console.log('🎭 [DevRoleOverride] ✅ Override activated:', { 
        roleId, 
        previous: prevRoleId,
        status: 'ACTIVE'
      });
      console.log('🎭 [DevRoleOverride] 🔄 All permission checks will now use this role');
    } else {
      console.log('🎭 [DevRoleOverride] ❌ Override disabled:', {
        previous: prevRoleId,
        status: 'INACTIVE'
      });
    }
  },
  
  clearOverride: () => {
    const prevRoleId = get().overrideRoleId;
    set({ overrideRoleId: null });
    console.log('🎭 [DevRoleOverride] 🧹 Override cleared:', {
      previous: prevRoleId,
      status: 'CLEARED'
    });
    console.log('🎭 [DevRoleOverride] 🔄 Permission checks will use actual user role');
  },
  
  isEnabled: () => {
    return isDevelopmentMode() && get().overrideRoleId !== null;
  },
}));

/**
 * Get the effective role for permission checking
 * Returns override role if set, otherwise returns the user's actual role
 */
export function getEffectiveRole(companyId: string): string | null {
  const { overrideRoleId, isEnabled } = useDevRoleOverride.getState();
  
  // If override is enabled and we're in dev mode, return override role
  if (isEnabled() && overrideRoleId) {
    const roles = getCompanyRoles(companyId);
    const overrideRole = roles.find(r => r.id === overrideRoleId);
    if (overrideRole) {
      return overrideRole.role_name;
    }
  }
  
  // Otherwise return actual user role
  const userPerms = getUserPermissions(companyId);
  return userPerms?.role || null;
}

/**
 * Get the user's actual role (ignoring any dev override)
 * Useful for displaying the real role in UI
 */
export function getActualRole(companyId: string): string | null {
  const userPerms = getUserPermissions(companyId);
  return userPerms?.role || null;
}

/**
 * Get the effective role permissions for permission checking
 * Returns override role permissions if set, otherwise returns the user's actual permissions
 */
export function getEffectivePermissions(companyId: string) {
  const { overrideRoleId, isEnabled } = useDevRoleOverride.getState();
  
  // If override is enabled and we're in dev mode, return override role permissions
  if (isEnabled() && overrideRoleId) {
    const roles = getCompanyRoles(companyId);
    const overrideRole = roles.find(r => r.id === overrideRoleId);
    if (overrideRole) {
      return overrideRole.permissions;
    }
  }
  
  // Otherwise return actual user permissions
  const userPerms = getUserPermissions(companyId);
  return userPerms?.permissions || {};
}

